import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Upload, Pencil, Check, X, ChevronDown, ChevronRight, Send } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);

function Sparkline({ points, improved, target }) {
  if (!points || points.length < 2) return <span className="text-xs text-muted-foreground">-</span>;
  const vals = points.map((p) => p.value);
  let min = Math.min(...vals), max = Math.max(...vals);
  if (target !== null && target !== undefined) { min = Math.min(min, target); max = Math.max(max, target); }
  const rng = max - min || 1;
  const w = 84, h = 22;
  const d = points.map((p, i) => `${(i / (points.length - 1)) * w},${h - ((p.value - min) / rng) * h}`).join(" ");
  const ty = target !== null && target !== undefined ? h - ((target - min) / rng) * h : null;
  return (
    <svg width={w} height={h} className="overflow-visible">
      {ty !== null && <line x1="0" y1={ty} x2={w} y2={ty} strokeWidth="1" strokeDasharray="3 2" className="stroke-muted-foreground" />}
      <polyline points={d} fill="none" strokeWidth="1.5" className={improved ? "stroke-traffic-green" : "stroke-traffic-red"} />
    </svg>
  );
}

const num = (v) => (v === null || v === undefined ? "-" : v);
const ATTRS = ["strength", "speed", "agility", "endurance", "flexibility"];

export default function SectionStats({ athleteId, section, label, presets = [], tag = null, silo = null, notes = {}, onAddNote = null, notesOnly = false, onEmpty = null, alwaysShow = false }) {
  const [d, setD] = useState(null);
  const [logDate, setLogDate] = useState(today());
  const [rowVals, setRowVals] = useState({});
  const [newDef, setNewDef] = useState({ name: "", unit: "", direction: "up", target: "", attribute: "", body_part: "" });
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editBuf, setEditBuf] = useState({});
  const [csv, setCsv] = useState("");
  const [showCsv, setShowCsv] = useState(false);
  const [csvCols, setCsvCols] = useState(null);
  const [csvConfig, setCsvConfig] = useState({});
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteTxt, setNoteTxt] = useState("");
  const [actOpen, setActOpen] = useState(true);

  const load = useCallback(() => {
    if (athleteId) api.get(`/athletes/${athleteId}/section-stats?section=${section}`).then((r) => setD(r.data));
  }, [athleteId, section]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const onSynced = () => load();
    window.addEventListener("trifit-synced", onSynced);
    return () => window.removeEventListener("trifit-synced", onSynced);
  }, [load]);
  useEffect(() => {
    if (d && onEmpty) {
      const empty = !notesOnly && Object.keys(d.by_activity || {}).length === 0 && d.defs.length === 0 && d.log_count === 0;
      onEmpty(section, empty);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [d]);

  const toTarget = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
  const isSnc = section === "snc";
  const addDef = async (name = newDef.name, extra = newDef) => {
    if (!name.trim()) return toast.error("Metric name required");
    await api.post(`/athletes/${athleteId}/metric-defs`, { section, name: name.trim(), unit: extra.unit || "", direction: extra.direction || "up", target: toTarget(extra.target), attribute: extra.attribute || "", body_part: extra.body_part || "" });
    toast.success("Metric added"); setNewDef({ name: "", unit: "", direction: "up", target: "", attribute: "", body_part: "" }); setShowAdd(false); load();
  };
  const delDef = async (id) => { await api.delete(`/athletes/${athleteId}/metric-defs/${id}`); load(); };
  const startEdit = (def) => { setEditing(def.def_id); setEditBuf({ name: def.name, unit: def.unit || "", direction: def.direction || "up", target: def.target ?? "", attribute: def.attribute || "", body_part: def.body_part || "" }); };
  const saveEdit = async (def_id) => {
    await api.put(`/athletes/${athleteId}/metric-defs/${def_id}`, { section, name: editBuf.name, unit: editBuf.unit, direction: editBuf.direction, target: toTarget(editBuf.target), attribute: editBuf.attribute || "", body_part: editBuf.body_part || "" });
    toast.success("Metric updated"); setEditing(null); load();
  };

  const logValue = async (name) => {
    const v = rowVals[name];
    if (v === undefined || v === "") return toast.error("Enter a value");
    await api.post(`/athletes/${athleteId}/section-log`, { section, date: logDate, title: label, metrics: { [name]: v } });
    toast.success(`Logged ${name}`); setRowVals({ ...rowVals, [name]: "" }); load();
  };
  const logRow = async (activity, name, key) => {
    const v = rowVals[key];
    if (v === undefined || v === "") return toast.error("Enter a value");
    await api.post(`/athletes/${athleteId}/section-log`, { section, date: logDate, title: activity, metrics: { [name]: v } });
    toast.success(`Logged ${name} · ${activity}`); setRowVals({ ...rowVals, [key]: "" }); load();
  };
  const setActivityAttr = async (activity, attribute) => {
    await api.post(`/athletes/${athleteId}/activity-attribute`, { activity, attribute });
    load();
  };
  const [newAct, setNewAct] = useState({ title: "", attribute: "", metric: "", value: "" });
  const [showActForm, setShowActForm] = useState(false);
  const DAY_TYPE_OPTS = ["snc", "sports_skill", "travel", "match", "rest", "recovery"];
  const [ratScope, setRatScope] = useState("global");
  const [ratKey, setRatKey] = useState("");
  const [rat, setRat] = useState({ calories: "", protein_pct: "", fat_pct: "", carbs_pct: "", fibre_g: "", hydration_ml: "", steps: "", calorie_burn: "" });
  const [showGoals, setShowGoals] = useState(false);
  useEffect(() => {
    if (section !== "nutrition" || !d) return;
    let src = {};
    if (ratScope === "global") src = d.nutrition_ratio || {};
    else if (ratScope === "day_type") src = (d.nutrition_day_ratios || {})[ratKey] || {};
    else if (ratScope === "date") src = (d.nutrition_day_overrides || {})[ratKey] || {};
    setRat({ calories: src.calories ?? "", protein_pct: src.protein_pct ?? "", fat_pct: src.fat_pct ?? "",
             carbs_pct: src.carbs_pct ?? "", fibre_g: src.fibre_g ?? "", hydration_ml: src.hydration_ml ?? "",
             steps: src.steps ?? "", calorie_burn: src.calorie_burn ?? "" });
  }, [d, ratScope, ratKey, section]);
  const ratCal = Number(rat.calories) || 0;
  const pctSum = (Number(rat.protein_pct) || 0) + (Number(rat.fat_pct) || 0) + (Number(rat.carbs_pct) || 0);
  const gP = ratCal ? Math.round(ratCal * (Number(rat.protein_pct) || 0) / 100 / 4) : 0;
  const gF = ratCal ? Math.round(ratCal * (Number(rat.fat_pct) || 0) / 100 / 9) : 0;
  const gC = ratCal ? Math.round(ratCal * (Number(rat.carbs_pct) || 0) / 100 / 4) : 0;
  const pushToday = async () => {
    try {
      const r = await api.post(`/athletes/${athleteId}/nutrition-ratio/push-today`);
      const g = r.data.grams || {};
      toast.success(`Pushed today (${r.data.day_type}, ${r.data.source}): ${g.protein_g ?? "-"}P / ${g.fat_g ?? "-"}F / ${g.carbs_g ?? "-"}C g @ ${r.data.calories} kcal`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Push failed"); }
  };
  const saveRatio = async (push) => {
    if (ratScope !== "global" && !ratKey) { toast.error(ratScope === "day_type" ? "Pick a day type" : "Pick a date"); return; }
    if (pctSum && Math.abs(pctSum - 100) > 0.5) { toast.error(`Macros must total 100% (now ${Math.round(pctSum)}%)`); return; }
    const body = { scope: ratScope, key: ratScope === "global" ? "" : ratKey, push_to_phone: push };
    ["calories", "protein_pct", "fat_pct", "carbs_pct", "fibre_g", "hydration_ml", "steps", "calorie_burn"]
      .forEach((k) => { if (rat[k] !== "" && !isNaN(Number(rat[k]))) body[k] = Number(rat[k]); });
    try {
      const r = await api.post(`/athletes/${athleteId}/nutrition-ratio`, body);
      const gm = r.data.grams || {};
      if (push) toast.success(r.data.pushed?.ok ? `Pushed to phone: ${gm.protein_g ?? "-"}P / ${gm.fat_g ?? "-"}F / ${gm.carbs_g ?? "-"}C g` : (r.data.pushed?.note || "Saved (no phone fields)"));
      else toast.success(`Saved (${ratScope === "global" ? "default" : ratKey}): ${gm.protein_g ?? "-"}P / ${gm.fat_g ?? "-"}F / ${gm.carbs_g ?? "-"}C g`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const [editTgt, setEditTgt] = useState(null);
  const [tgtVal, setTgtVal] = useState("");
  const saveTarget = async (name, unit) => {
    try {
      await api.put(`/athletes/${athleteId}/metric-target`, { section, name, unit: unit || "", target: tgtVal === "" ? null : Number(tgtVal) });
      toast.success("Target updated"); setEditTgt(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const deleteActivity = async (title) => {
    if (!window.confirm(`Delete all logged "${title}" sessions in ${label}? This cannot be undone.`)) return;
    try { const r = await api.delete(`/athletes/${athleteId}/activities`, { params: { section, title } }); toast.success(`Deleted ${r.data.deleted} entries`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const [renamingAct, setRenamingAct] = useState(null);
  const [renameActVal, setRenameActVal] = useState("");
  const renameActivity = async (oldTitle) => {
    const newTitle = renameActVal.trim();
    if (!newTitle) return toast.error("New name required");
    try {
      const r = await api.put(`/athletes/${athleteId}/activity-rename`, { section, old_title: oldTitle, new_title: newTitle });
      toast.success(`Renamed to "${r.data.new_title}" (${r.data.renamed} row(s))`);
      setRenamingAct(null); setRenameActVal(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Rename failed"); }
  };
  const addActivity = async () => {
    if (!newAct.title.trim()) return toast.error("Activity name required");
    const metrics = {};
    if (newAct.metric && newAct.value !== "") metrics[newAct.metric] = isNaN(Number(newAct.value)) ? newAct.value : Number(newAct.value);
    await api.post(`/athletes/${athleteId}/section-log`, { section, date: logDate, title: newAct.title.trim(), metrics });
    if (newAct.attribute) await api.post(`/athletes/${athleteId}/activity-attribute`, { activity: newAct.title.trim(), attribute: newAct.attribute });
    toast.success("Activity added"); setNewAct({ title: "", attribute: "", metric: "", value: "" }); setShowActForm(false); load();
  };

  const parseCsvCols = () => {
    const firstLine = (csv.trim().split(/\r?\n/)[0] || "");
    const reserved = new Set(["date", "title", "name", "event", "day_type", "duration_min", "intensity", "result"]);
    const cols = firstLine.split(",").map((c) => c.trim()).filter((c) => c && !reserved.has(c.toLowerCase()));
    if (cols.length === 0) return toast.error("No metric columns detected in header row");
    setCsvCols(cols);
    setCsvConfig(Object.fromEntries(cols.map((c) => [c, { unit: "", direction: "up" }])));
  };
  const importCsv = async () => {
    if (!csv.trim()) return toast.error("Paste CSV first");
    try {
      const r = await api.post(`/athletes/${athleteId}/section-import`, { section, csv, metric_config: csvConfig });
      toast.success(`Imported ${r.data.created} rows · ${r.data.metric_defs_added} new metrics`);
      setCsv(""); setShowCsv(false); setCsvCols(null); setCsvConfig({}); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); }
  };

  if (!d) return <div className="text-sm text-muted-foreground">Loading {label}…</div>;
  const defs = d.defs;
  const series = d.series;
  const acts = Object.entries(d.by_activity || {});
  const hasData = acts.length > 0;
  const isSncDrills = section === "snc" || section === "drills";
  // Hide completely unused domains (no logged data and no configured metrics) · unless alwaysShow
  if (!alwaysShow && !notesOnly && !hasData && defs.length === 0 && d.log_count === 0) return null;
  const rowNames = [...new Set([...defs.map((x) => x.name), ...Object.keys(series)])];
  const availPresets = presets.filter((p) => !defs.some((x) => x.name === p));

  const noteList = (silo && notes[silo]) || [];
  const notesBlock = silo && onAddNote ? (
    <div className="p-5 border-t border-border" data-testid={`notes-zone-${silo}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground flex-1">Notes</span>
        <button data-testid={`add-note-${silo}`} onClick={() => setNoteOpen((o) => !o)} className="text-xs border border-border px-2 py-1 hover:bg-surface transition-colors">+ Note</button>
      </div>
      {noteOpen && (
        <div className="flex gap-2 mb-3">
          <input data-testid={`note-input-${silo}`} value={noteTxt} onChange={(e) => setNoteTxt(e.target.value)} placeholder={`Note for ${label}…`} className="inp text-sm flex-1" />
          <button data-testid={`note-save-${silo}`} onClick={async () => { if (noteTxt.trim()) { await onAddNote(silo, noteTxt); setNoteTxt(""); setNoteOpen(false); } }} className="text-sm px-3 bg-foreground text-background hover:bg-neutral-800 transition-colors">Save</button>
        </div>
      )}
      {noteList.length === 0 ? <p className="text-xs text-muted-foreground">No notes yet.</p> : (
        <div className="border border-border divide-y divide-border bg-surface" data-testid={`notes-${silo}`}>
          {noteList.map((n) => <div key={n.note_id} className="p-2.5 text-sm">{n.text} <span className="text-xs text-muted-foreground">- {n.by}</span></div>)}
        </div>
      )}
    </div>
  ) : null;

  return (
    <div className="border border-border" data-testid={`section-${section}`}>
      <div className="px-5 py-4 border-b border-border flex items-center gap-3 flex-wrap">
        <h3 className="font-heading font-bold tracking-tight flex items-center gap-2">{label} <span className="text-xs text-muted-foreground font-normal">· {d.log_count} logs</span></h3>
        {tag && silo !== "drills" && silo !== "snc" && <span data-testid={`tag-${section}`} className="text-[10px] tracking-[0.15em] uppercase font-bold px-2 py-1 border border-foreground">{tag}</span>}
        <span className="flex-1" />
        {!notesOnly && (<>
        <label className="text-xs text-muted-foreground flex items-center gap-1.5">Log date
          <input type="date" data-testid={`log-date-${section}`} value={logDate} onChange={(e) => setLogDate(e.target.value)} className="inp text-sm" />
        </label>
        <button data-testid={`add-toggle-${section}`} onClick={() => setShowAdd((s) => !s)} className="text-xs border border-border px-2 py-1 hover:bg-surface transition-colors flex items-center gap-1"><Plus className="w-3 h-3" /> Metric</button>
        <button data-testid={`csv-toggle-${section}`} onClick={() => setShowCsv((s) => !s)} className="text-xs border border-border px-2 py-1 hover:bg-surface transition-colors flex items-center gap-1"><Upload className="w-3 h-3" /> CSV</button>
        </>)}
      </div>

      {notesOnly ? notesBlock : (<>
      {/* Add metric (name is a dropdown of presets + custom) */}
      {showAdd && (
        <div className="p-4 border-b border-border bg-surface flex flex-wrap gap-2 items-center" data-testid={`add-metric-${section}`}>
          <input list={`metric-opts-${section}`} data-testid={`newdef-name-${section}`} value={newDef.name} onChange={(e) => setNewDef({ ...newDef, name: e.target.value })} placeholder="metric name (pick or type)" className="inp text-sm flex-1 min-w-[180px]" />
          <datalist id={`metric-opts-${section}`}>{presets.map((p) => <option key={p} value={p} />)}</datalist>
          <input data-testid={`newdef-unit-${section}`} value={newDef.unit} onChange={(e) => setNewDef({ ...newDef, unit: e.target.value })} placeholder="unit" className="inp text-sm w-20" />
          <select data-testid={`newdef-dir-${section}`} value={newDef.direction} onChange={(e) => setNewDef({ ...newDef, direction: e.target.value })} className="inp text-sm w-36"><option value="up">higher better</option><option value="down">lower better</option></select>
          <input data-testid={`newdef-target-${section}`} value={newDef.target} onChange={(e) => setNewDef({ ...newDef, target: e.target.value })} placeholder="target" className="inp text-sm w-20" />
          {isSnc && (
            <>
              <select data-testid={`newdef-attr-${section}`} value={newDef.attribute} onChange={(e) => setNewDef({ ...newDef, attribute: e.target.value })} className="inp text-sm w-32"><option value="">attribute…</option>{ATTRS.map((a) => <option key={a} value={a}>{a}</option>)}</select>
              <input data-testid={`newdef-bodypart-${section}`} value={newDef.body_part} onChange={(e) => setNewDef({ ...newDef, body_part: e.target.value })} placeholder="body part (legs/core…)" className="inp text-sm w-40" />
            </>
          )}
          <button data-testid={`add-def-${section}`} onClick={() => addDef()} className="text-xs bg-foreground text-background px-3 py-2 hover:bg-neutral-800 transition-colors">Add</button>
          {availPresets.length > 0 && (
            <div className="w-full flex flex-wrap gap-1.5 pt-1" data-testid={`presets-${section}`}>
              <span className="text-xs text-muted-foreground self-center">Quick add:</span>
              {availPresets.map((p) => <button key={p} data-testid={`preset-${section}-${p}`} onClick={() => addDef(p, { unit: "", direction: "up", target: "" })} className="text-xs border border-border px-2 py-0.5 hover:bg-background transition-colors">+ {p}</button>)}
            </div>
          )}
        </div>
      )}

      {/* CSV import with column mapping */}
      {showCsv && (
        <div className="p-4 border-b border-border bg-surface" data-testid={`csv-panel-${section}`}>
          <p className="text-xs text-muted-foreground mb-2">Columns: <code>date,title{section === "match" ? ",result" : ",day_type,duration_min,intensity"}</code> + metric columns. Paste, then <b>Preview mapping</b>.</p>
          <textarea data-testid={`csv-input-${section}`} value={csv} onChange={(e) => { setCsv(e.target.value); setCsvCols(null); }} rows={3} placeholder={`date,title,${section === "match" ? "result,goals" : "squat_1rm,vertical_jump"}\n2026-07-01,Test,...`} className="inp text-sm font-mono w-full" />
          {!csvCols ? (
            <button data-testid={`csv-preview-${section}`} onClick={parseCsvCols} className="mt-2 border border-foreground px-4 py-2 text-sm hover:bg-background transition-colors">Preview mapping</button>
          ) : (
            <div className="mt-3" data-testid={`csv-mapping-${section}`}>
              <div className="space-y-2 mb-3">
                {csvCols.map((col) => (
                  <div key={col} className="flex gap-2 items-center text-sm" data-testid={`csv-col-${section}-${col}`}>
                    <span className="font-medium flex-1">{col}</span>
                    <input value={csvConfig[col]?.unit || ""} onChange={(e) => setCsvConfig({ ...csvConfig, [col]: { ...csvConfig[col], unit: e.target.value } })} placeholder="unit" className="inp text-sm w-24" data-testid={`csv-unit-${section}-${col}`} />
                    <select value={csvConfig[col]?.direction || "up"} onChange={(e) => setCsvConfig({ ...csvConfig, [col]: { ...csvConfig[col], direction: e.target.value } })} className="inp text-sm w-36" data-testid={`csv-dir-${section}-${col}`}><option value="up">higher better</option><option value="down">lower better</option></select>
                  </div>
                ))}
              </div>
              <button data-testid={`csv-import-${section}`} onClick={importCsv} className="bg-foreground text-background px-4 py-2 text-sm flex items-center gap-2 hover:bg-neutral-800 transition-colors"><Upload className="w-4 h-4" /> Import {csvCols.length} metrics</button>
            </div>
          )}
        </div>
      )}

      {/* Activity-first view: each activity → its metrics with rolling windows, target & inline log */}
      <div className="px-5 pt-4 flex items-center gap-2" data-testid={`add-activity-bar-${section}`}>
        <button onClick={() => setShowActForm((s) => !s)} data-testid={`toggle-add-activity-${section}`} className="text-xs flex items-center gap-1 border border-border px-3 py-1.5 hover:bg-surface transition-colors"><Plus className="w-3.5 h-3.5" /> Add activity</button>
        {section === "nutrition" && (
          <button onClick={() => setShowGoals((s) => !s)} data-testid="toggle-nutrition-goals" className="text-xs flex items-center gap-1 border border-foreground bg-foreground text-background px-3 py-1.5 hover:bg-neutral-800 transition-colors"><Plus className="w-3.5 h-3.5" /> Calories &amp; macro ratios</button>
        )}
        {section === "nutrition" && (
          <button onClick={pushToday} data-testid="push-today-ratio" className="text-xs flex items-center gap-1 border border-border px-3 py-1.5 hover:bg-surface transition-colors"><Send className="w-3.5 h-3.5" /> Push today's ratio</button>
        )}
        {showActForm && (
          <div className="flex flex-wrap items-center gap-2">
            <input data-testid={`newact-title-${section}`} value={newAct.title} onChange={(e) => setNewAct({ ...newAct, title: e.target.value })} placeholder="activity name" className="inp text-sm w-40" />
            {isSncDrills && <select data-testid={`newact-attr-${section}`} value={newAct.attribute} onChange={(e) => setNewAct({ ...newAct, attribute: e.target.value })} className="inp text-sm w-28"><option value="">attribute…</option>{ATTRS.map((x) => <option key={x} value={x}>{x}</option>)}</select>}
            <input data-testid={`newact-metric-${section}`} value={newAct.metric} onChange={(e) => setNewAct({ ...newAct, metric: e.target.value })} placeholder="metric (e.g. load_kg)" className="inp text-sm w-36" />
            <input data-testid={`newact-value-${section}`} value={newAct.value} onChange={(e) => setNewAct({ ...newAct, value: e.target.value })} placeholder="value" className="inp text-sm w-20" />
            <button data-testid={`save-activity-${section}`} onClick={addActivity} className="text-xs bg-foreground text-background px-3 py-1.5 hover:bg-neutral-800 transition-colors">Save</button>
          </div>
        )}
      </div>
      {section === "nutrition" && showGoals && (
        <div className="px-5 pb-3 pt-3 border-b border-border" data-testid="nutrition-goals-form">
          <p className="text-xs text-muted-foreground mb-3">Enter <b>calories</b> + a <b>macro % split</b> (protein / fat / carbs · must total 100%) and the grams are computed for you. Set a <b>Default</b>, a ratio per <b>day type</b> (rest vs match vs S&amp;C…), or a one-off for a single <b>date</b> without touching templates. Fibre is a raw gram number.</p>
          {/* scope selector */}
          <div className="flex flex-wrap items-end gap-3 mb-3">
            <label className="block"><span className="text-[10px] uppercase tracking-wider text-muted-foreground">Applies to</span>
              <select data-testid="ratio-scope" value={ratScope} onChange={(e) => { setRatScope(e.target.value); setRatKey(""); }} className="inp text-sm w-40 mt-0.5">
                <option value="global">Default (all days)</option>
                <option value="day_type">A day type</option>
                <option value="date">A single date</option>
              </select>
            </label>
            {ratScope === "day_type" && (
              <label className="block"><span className="text-[10px] uppercase tracking-wider text-muted-foreground">Day type</span>
                <select data-testid="ratio-daytype" value={ratKey} onChange={(e) => setRatKey(e.target.value)} className="inp text-sm w-40 mt-0.5">
                  <option value="">pick…</option>{DAY_TYPE_OPTS.map((x) => <option key={x} value={x}>{x.replace("_", " ")}</option>)}
                </select>
              </label>
            )}
            {ratScope === "date" && (
              <label className="block"><span className="text-[10px] uppercase tracking-wider text-muted-foreground">Date</span>
                <input type="date" data-testid="ratio-date" value={ratKey} onChange={(e) => setRatKey(e.target.value)} className="inp text-sm w-40 mt-0.5" />
              </label>
            )}
          </div>
          {/* calories + macro % */}
          <div className="flex flex-wrap items-end gap-3">
            <label className="block"><span className="text-[10px] uppercase tracking-wider text-muted-foreground">Calories (kcal)</span><input data-testid="ratio-calories" value={rat.calories} onChange={(e) => setRat({ ...rat, calories: e.target.value })} className="inp text-sm w-24 mt-0.5" /></label>
            <label className="block"><span className="text-[10px] uppercase tracking-wider text-muted-foreground">Protein %</span><input data-testid="ratio-protein-pct" value={rat.protein_pct} onChange={(e) => setRat({ ...rat, protein_pct: e.target.value })} className="inp text-sm w-20 mt-0.5" /></label>
            <label className="block"><span className="text-[10px] uppercase tracking-wider text-muted-foreground">Fat %</span><input data-testid="ratio-fat-pct" value={rat.fat_pct} onChange={(e) => setRat({ ...rat, fat_pct: e.target.value })} className="inp text-sm w-20 mt-0.5" /></label>
            <label className="block"><span className="text-[10px] uppercase tracking-wider text-muted-foreground">Carbs %</span><input data-testid="ratio-carbs-pct" value={rat.carbs_pct} onChange={(e) => setRat({ ...rat, carbs_pct: e.target.value })} className="inp text-sm w-20 mt-0.5" /></label>
            <span className={`text-xs px-2 py-1 border ${pctSum && Math.abs(pctSum - 100) > 0.5 ? "border-traffic-red text-traffic-red" : "border-border text-muted-foreground"}`} data-testid="ratio-pct-sum">Σ {Math.round(pctSum)}%</span>
          </div>
          {/* computed grams answer */}
          <div className="flex flex-wrap items-center gap-4 mt-3 text-sm" data-testid="ratio-grams-preview">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Computed</span>
            <span className="font-heading font-bold" data-testid="ratio-grams-protein">Protein {gP} g</span>
            <span className="font-heading font-bold" data-testid="ratio-grams-fat">Fat {gF} g</span>
            <span className="font-heading font-bold" data-testid="ratio-grams-carbs">Carbs {gC} g</span>
          </div>
          {/* raw fibre + hydration + steps + burn */}
          <div className="flex flex-wrap items-end gap-3 mt-3">
            <label className="block"><span className="text-[10px] uppercase tracking-wider text-muted-foreground">Fibre (g, raw)</span><input data-testid="ratio-fibre" value={rat.fibre_g} onChange={(e) => setRat({ ...rat, fibre_g: e.target.value })} className="inp text-sm w-20 mt-0.5" /></label>
            <label className="block"><span className="text-[10px] uppercase tracking-wider text-muted-foreground">Hydration (ml)</span><input data-testid="ratio-hydration" value={rat.hydration_ml} onChange={(e) => setRat({ ...rat, hydration_ml: e.target.value })} className="inp text-sm w-24 mt-0.5" /></label>
            <label className="block"><span className="text-[10px] uppercase tracking-wider text-muted-foreground">Steps</span><input data-testid="ratio-steps" value={rat.steps} onChange={(e) => setRat({ ...rat, steps: e.target.value })} className="inp text-sm w-24 mt-0.5" /></label>
            <label className="block"><span className="text-[10px] uppercase tracking-wider text-muted-foreground">Calories burnt</span><input data-testid="ratio-burn" value={rat.calorie_burn} onChange={(e) => setRat({ ...rat, calorie_burn: e.target.value })} className="inp text-sm w-24 mt-0.5" /></label>
            <button data-testid="save-goals" onClick={() => saveRatio(false)} className="text-xs border border-border px-3 py-2 hover:bg-surface transition-colors">Save</button>
            <button data-testid="push-goals" onClick={() => saveRatio(true)} className="text-xs bg-foreground text-background px-3 py-2 hover:bg-neutral-800 transition-colors">Save &amp; push to phone</button>
          </div>
          <p className="text-[11px] text-muted-foreground mt-2">Calories, hydration &amp; steps sync to the phone; macros are written via <code>microRatio</code> (the phone's Nutrition rings may need a Trifit app update to display them).</p>
        </div>
      )}
      {section === "nutrition" && d && Object.keys(d.nutrition_day_ratios || {}).length > 0 && (
        <div className="px-5 pb-3 pt-3 border-b border-border" data-testid="nutrition-daytype-grid">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Macro targets by day type</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-[10px] tracking-[0.15em] uppercase font-bold text-muted-foreground border-b border-border">
                <th className="text-left p-2">Day type</th><th className="text-right p-2">kcal</th>
                <th className="text-right p-2">P%</th><th className="text-right p-2">F%</th><th className="text-right p-2">C%</th>
                <th className="text-right p-2">P/F/C g</th><th className="text-right p-2">Fibre</th></tr></thead>
              <tbody>
                {Object.entries(d.nutrition_day_ratios).map(([dt, r]) => {
                  const cal = Number(r.calories) || 0;
                  const g = (p, div) => cal ? Math.round(cal * (Number(p) || 0) / 100 / div) : 0;
                  return (
                    <tr key={dt} className="border-b border-border last:border-0" data-testid={`daytype-row-${dt}`}>
                      <td className="p-2 font-medium capitalize">{dt.replace("_", " ")}</td>
                      <td className="p-2 text-right tabular-nums">{r.calories ?? "-"}</td>
                      <td className="p-2 text-right tabular-nums text-muted-foreground">{r.protein_pct ?? "-"}</td>
                      <td className="p-2 text-right tabular-nums text-muted-foreground">{r.fat_pct ?? "-"}</td>
                      <td className="p-2 text-right tabular-nums text-muted-foreground">{r.carbs_pct ?? "-"}</td>
                      <td className="p-2 text-right tabular-nums font-heading font-bold">{g(r.protein_pct, 4)}/{g(r.fat_pct, 9)}/{g(r.carbs_pct, 4)}</td>
                      <td className="p-2 text-right tabular-nums text-muted-foreground">{r.fibre_g ?? "-"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <div className="overflow-x-auto" data-testid={`activity-view-${section}`}>
        {!hasData ? (
          <p className="p-5 text-sm text-muted-foreground">No logged sessions yet. Add a metric or log a value to start tracking by activity.</p>
        ) : (
          <table className="w-full text-sm" data-testid={`activity-table-${section}`}>
            <thead>
              <tr className="border-b border-border text-[10px] tracking-[0.15em] uppercase font-bold text-muted-foreground">
                <th className="text-left p-3">Activity</th>
                {isSncDrills && <th className="text-left p-3">Attribute</th>}
                <th className="text-left p-3">Metric</th>
                <th className="text-left p-3">Trend</th>
                <th className="text-right p-3">Today</th>
                <th className="text-right p-3">L7</th>
                <th className="text-right p-3">L30</th>
                <th className="text-right p-3">L90</th>
                <th className="text-right p-3">Base</th>
                <th className="text-right p-3">Δ base</th>
                <th className="text-right p-3">Target</th>
                <th className="text-right p-3">Log ({logDate.slice(5)})</th>
              </tr>
            </thead>
            <tbody>
              {acts.map(([ttl, info]) => {
                const mets = Object.entries(info.metrics);
                return mets.map(([name, mv], mi) => {
                  const key = `${ttl}::${name}`;
                  return (
                    <tr key={key} className="border-b border-border last:border-0" data-testid={`activity-row-${section}-${ttl}-${name}`}>
                      {mi === 0 && (
                        <td className="p-3 align-top font-medium border-r border-border" rowSpan={mets.length}>
                          <div className="flex items-start gap-1.5">
                            <span className="flex-1">
                              {renamingAct === ttl ? (
                                <span className="inline-flex items-center gap-1">
                                  <input autoFocus data-testid={`rename-activity-input-${section}-${ttl}`}
                                    value={renameActVal} onChange={(e) => setRenameActVal(e.target.value)}
                                    onKeyDown={(e) => e.key === "Enter" && renameActivity(ttl)} className="inp text-xs w-40" />
                                  <button onClick={() => renameActivity(ttl)} className="text-traffic-green" data-testid={`rename-activity-save-${section}-${ttl}`}><Check className="w-3.5 h-3.5" /></button>
                                  <button onClick={() => setRenamingAct(null)} className="text-muted-foreground"><X className="w-3.5 h-3.5" /></button>
                                </span>
                              ) : (
                                <>
                                  {ttl}
                                  <div className="text-[10px] text-muted-foreground font-normal">{info.sessions} session(s)</div>
                                </>
                              )}
                            </span>
                            {renamingAct !== ttl && (
                              <>
                                <button data-testid={`rename-activity-${section}-${ttl}`} onClick={() => { setRenamingAct(ttl); setRenameActVal(ttl); }} title="Rename this activity" className="text-muted-foreground/60 hover:text-foreground transition-colors"><Pencil className="w-3.5 h-3.5" /></button>
                                <button data-testid={`del-activity-${section}-${ttl}`} onClick={() => deleteActivity(ttl)} title="Delete this activity's logs" className="text-muted-foreground/60 hover:text-traffic-red transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                              </>
                            )}
                          </div>
                        </td>
                      )}
                      {isSncDrills && mi === 0 && (
                        <td className="p-3 align-top border-r border-border" rowSpan={mets.length} data-testid={`activity-attr-${section}-${ttl}`}>
                          <select value={info.attribute || ""} onChange={(e) => setActivityAttr(ttl, e.target.value)} className="inp text-xs w-28" data-testid={`attr-select-${section}-${ttl}`}>
                            <option value="">- none -</option>
                            {ATTRS.map((x) => <option key={x} value={x}>{x}</option>)}
                          </select>
                        </td>
                      )}
                      <td className="p-3 font-medium">
                        {(() => {
                          const def = defs.find((x) => x.name === name);
                          if (editing && def && editing === def.def_id) {
                            return (
                              <span className="inline-flex items-center gap-1">
                                <input autoFocus data-testid={`edit-name-${section}-${name}`} value={editBuf.name}
                                  onChange={(e) => setEditBuf({ ...editBuf, name: e.target.value })}
                                  onKeyDown={(e) => e.key === "Enter" && saveEdit(def.def_id)}
                                  className="inp text-xs w-32" />
                                <input data-testid={`edit-unit-${section}-${name}`} value={editBuf.unit}
                                  onChange={(e) => setEditBuf({ ...editBuf, unit: e.target.value })}
                                  placeholder="unit" className="inp text-xs w-16" />
                                <button onClick={() => saveEdit(def.def_id)} className="text-traffic-green" data-testid={`save-edit-${section}-${name}`}><Check className="w-3.5 h-3.5" /></button>
                                <button onClick={() => setEditing(null)} className="text-muted-foreground"><X className="w-3.5 h-3.5" /></button>
                              </span>
                            );
                          }
                          return (
                            <span className="inline-flex items-center gap-1.5 flex-wrap">
                              <span>{name}{mv.unit ? <span className="text-muted-foreground"> ({mv.unit})</span> : null}</span>
                              {def && (
                                <span className="inline-flex gap-1">
                                  <button data-testid={`rename-metric-${section}-${name}`} title="Rename metric"
                                    onClick={() => startEdit(def)} className="text-muted-foreground/60 hover:text-foreground transition-colors"><Pencil className="w-3 h-3" /></button>
                                  <button data-testid={`delete-metric-${section}-${name}`} title="Delete this metric"
                                    onClick={() => { if (window.confirm(`Delete metric "${name}" from ${label}? Logged values stay in the timeline.`)) delDef(def.def_id); }}
                                    className="text-muted-foreground/60 hover:text-traffic-red transition-colors"><Trash2 className="w-3 h-3" /></button>
                                </span>
                              )}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="p-3"><Sparkline points={mv.points} improved={mv.improved} target={mv.target} /></td>
                      <td className="p-3 text-right font-heading font-bold tabular-nums">{num(mv.today)}</td>
                      <td className="p-3 text-right tabular-nums text-muted-foreground">{num(mv.l7)}</td>
                      <td className="p-3 text-right tabular-nums text-muted-foreground">{num(mv.l30)}</td>
                      <td className="p-3 text-right tabular-nums text-muted-foreground">{num(mv.l90)}</td>
                      <td className="p-3 text-right tabular-nums text-muted-foreground">{num(mv.baseline)}</td>
                      <td className="p-3 text-right">
                        {mv.delta_pct !== null && mv.delta_pct !== undefined ? (
                          <span className={`text-xs px-1.5 py-0.5 border tabular-nums ${mv.improved ? "border-traffic-green text-traffic-green" : "border-traffic-red text-traffic-red"}`} data-testid={`delta-${section}-${ttl}-${name}`}>{mv.delta_pct > 0 ? "+" : ""}{mv.delta_pct}%</span>
                        ) : <span className="text-muted-foreground">-</span>}
                      </td>
                      <td className="p-3 text-right">
                        {editTgt === key ? (
                          <span className="inline-flex items-center gap-1">
                            <input autoFocus data-testid={`target-input-${section}-${ttl}-${name}`} value={tgtVal} onChange={(e) => setTgtVal(e.target.value)} onKeyDown={(e) => e.key === "Enter" && saveTarget(name, mv.unit)} placeholder="target" className="inp text-xs w-16" />
                            <button onClick={() => saveTarget(name, mv.unit)} className="text-traffic-green" data-testid={`target-save-${section}-${ttl}-${name}`}><Check className="w-3.5 h-3.5" /></button>
                            <button onClick={() => setEditTgt(null)} className="text-muted-foreground"><X className="w-3.5 h-3.5" /></button>
                          </span>
                        ) : (
                          <button data-testid={`target-${section}-${ttl}-${name}`} onClick={() => { setEditTgt(key); setTgtVal(mv.target ?? ""); }} className="text-xs hover:underline" title="Click to edit target">
                            {mv.target !== null && mv.target !== undefined ? (
                              <span className={mv.on_target ? "text-traffic-green" : "text-traffic-red"}>🎯{mv.target}{mv.on_target ? " ✓" : " ✗"}</span>
                            ) : <span className="text-muted-foreground"><Pencil className="w-3 h-3 inline" /> set</span>}
                          </button>
                        )}
                      </td>
                      <td className="p-3">
                        <div className="flex gap-1 justify-end">
                          <input data-testid={`log-input-${section}-${ttl}-${name}`} value={rowVals[key] ?? ""} onChange={(e) => setRowVals({ ...rowVals, [key]: e.target.value })} onKeyDown={(e) => e.key === "Enter" && logRow(ttl, name, key)} placeholder="value" className="inp text-sm w-20" />
                          <button data-testid={`log-btn-${section}-${ttl}-${name}`} onClick={() => logRow(ttl, name, key)} className="text-xs bg-foreground text-background px-2 hover:bg-neutral-800 transition-colors">Log</button>
                        </div>
                      </td>
                    </tr>
                  );
                });
              })}
            </tbody>
          </table>
        )}
      </div>
      {notesBlock}
      </>)}
    </div>
  );
}
